import React, { useState } from 'react';
import {
  Layers,
  ChevronRight,
  ChevronDown,
  Eye,
  EyeOff,
  MoveUp,
  MoveDown,
  Trash2,
  Box,
  Layout,
  Component,
  X,
} from 'lucide-react';

export default function NavigatorTree({
  document = {},
  selectedNodeId = null,
  onSelectNode,
  onMoveNode,
  onDeleteNode,
  onToggleVisibility,
  onClose,
}) {
  // Track expanded state of sections and containers
  const [collapsed, setCollapsed] = useState({});

  const toggleCollapse = (id, e) => {
    e.stopPropagation();
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const sections = document.sections || [];

  return (
    <div className="w-72 bg-white border-l border-slate-200 flex flex-col h-full select-none shadow-xs">
      {/* Header */}
      <div className="p-3.5 border-b border-slate-200 flex items-center justify-between bg-slate-50/70">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-[#2b3380]" />
          <h3 className="font-bold text-xs text-slate-800 uppercase tracking-wider">
            Navigator / Layers
          </h3>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Tree Content */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1 text-xs">
        {/* WEBSITE HEADER NODE */}
        <div
          onClick={() => onSelectNode('website-header')}
          className={`flex items-center justify-between px-2.5 py-2 rounded-lg cursor-pointer transition-colors mb-1 ${
            selectedNodeId === 'website-header'
              ? 'bg-[#2b3380] text-white font-bold shadow-xs'
              : 'hover:bg-slate-100 text-slate-700 border border-slate-100'
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Layout className={`w-3.5 h-3.5 ${selectedNodeId === 'website-header' ? 'text-indigo-200' : 'text-[#2b3380]'}`} />
            <span className="truncate text-xs font-semibold">Website Header & Nav</span>
          </div>
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
            selectedNodeId === 'website-header'
              ? 'bg-white/20 text-white'
              : 'bg-indigo-50 text-[#2b3380]'
          }`}>
            Header
          </span>
        </div>

        {sections.length === 0 ? (
          <div className="p-4 text-center text-slate-400 text-xs">No sections added yet.</div>
        ) : (
          sections.map((sec, secIdx) => {
            const isSecSelected = selectedNodeId === sec.id;
            const isSecCollapsed = collapsed[sec.id];
            const isSecVisible = sec.visibility?.desktop !== false;

            return (
              <div key={sec.id} className="space-y-0.5">
                {/* SECTION ROW */}
                <div
                  onClick={() => onSelectNode(sec.id)}
                  className={`flex items-center justify-between px-2 py-1.5 rounded-lg cursor-pointer transition-colors group ${
                    isSecSelected
                      ? 'bg-indigo-50 text-[#2b3380] font-bold border border-indigo-200'
                      : 'hover:bg-slate-100 text-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    <button
                      type="button"
                      onClick={(e) => toggleCollapse(sec.id, e)}
                      className="p-0.5 text-slate-400 hover:text-slate-700"
                    >
                      {isSecCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                    <Layout className="w-3.5 h-3.5 text-[#2b3380] flex-shrink-0" />
                    <span className="truncate text-xs">{sec.name || 'Section'}</span>
                  </div>

                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      disabled={secIdx === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMoveNode(sec.id, -1);
                      }}
                      className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-20"
                      title="Move Up"
                    >
                      <MoveUp className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      disabled={secIdx === sections.length - 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMoveNode(sec.id, 1);
                      }}
                      className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-20"
                      title="Move Down"
                    >
                      <MoveDown className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleVisibility(sec.id);
                      }}
                      className="p-1 text-slate-400 hover:text-slate-700"
                      title="Toggle Visibility"
                    >
                      {isSecVisible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3 text-amber-500" />}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteNode(sec.id);
                      }}
                      className="p-1 text-slate-400 hover:text-red-600"
                      title="Delete Section"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* CONTAINERS */}
                {!isSecCollapsed && Array.isArray(sec.children) && (
                  <div className="pl-4 space-y-0.5 border-l border-slate-200 ml-3">
                    {sec.children.map((con, conIdx) => {
                      const isConSelected = selectedNodeId === con.id;
                      const isConCollapsed = collapsed[con.id];
                      const isConVisible = con.visibility?.desktop !== false;

                      return (
                        <div key={con.id} className="space-y-0.5">
                          {/* CONTAINER ROW */}
                          <div
                            onClick={() => onSelectNode(con.id)}
                            className={`flex items-center justify-between px-2 py-1 rounded-md cursor-pointer transition-colors group ${
                              isConSelected
                                ? 'bg-indigo-50 text-[#2b3380] font-bold border border-indigo-200'
                                : 'hover:bg-slate-100 text-slate-600'
                            }`}
                          >
                            <div className="flex items-center gap-1.5 min-w-0">
                              <button
                                type="button"
                                onClick={(e) => toggleCollapse(con.id, e)}
                                className="p-0.5 text-slate-400 hover:text-slate-700"
                              >
                                {isConCollapsed ? (
                                  <ChevronRight className="w-2.5 h-2.5" />
                                ) : (
                                  <ChevronDown className="w-2.5 h-2.5" />
                                )}
                              </button>
                              <Box className="w-3 h-3 text-sky-600 flex-shrink-0" />
                              <span className="truncate text-[11px]">
                                {con.name || `Container (${con.layout || '1-col'})`}
                              </span>
                            </div>

                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                type="button"
                                disabled={conIdx === 0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onMoveNode(con.id, -1);
                                }}
                                className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20"
                              >
                                <MoveUp className="w-2.5 h-2.5" />
                              </button>
                              <button
                                type="button"
                                disabled={conIdx === sec.children.length - 1}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onMoveNode(con.id, 1);
                                }}
                                className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20"
                              >
                                <MoveDown className="w-2.5 h-2.5" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteNode(con.id);
                                }}
                                className="p-0.5 text-slate-400 hover:text-red-600"
                              >
                                <Trash2 className="w-2.5 h-2.5" />
                              </button>
                            </div>
                          </div>

                          {/* WIDGETS */}
                          {!isConCollapsed && Array.isArray(con.children) && (
                            <div className="pl-4 space-y-0.5 border-l border-slate-200 ml-3">
                              {con.children.map((wgt, wgtIdx) => {
                                const isWgtSelected = selectedNodeId === wgt.id;
                                const isWgtVisible = wgt.visibility?.desktop !== false;

                                return (
                                  <div
                                    key={wgt.id}
                                    onClick={() => onSelectNode(wgt.id)}
                                    className={`flex items-center justify-between px-2 py-1 rounded-md cursor-pointer transition-colors group ${
                                      isWgtSelected
                                        ? 'bg-[#2b3380] text-white font-bold shadow-xs'
                                        : 'hover:bg-slate-100 text-slate-500'
                                    }`}
                                  >
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      <Component className="w-2.5 h-2.5 flex-shrink-0 opacity-70" />
                                      <span className="truncate text-[11px]">
                                        {wgt.name || wgt.type}
                                      </span>
                                    </div>

                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <button
                                        type="button"
                                        disabled={wgtIdx === 0}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onMoveNode(wgt.id, -1);
                                        }}
                                        className="p-0.5 hover:text-slate-200 disabled:opacity-20"
                                      >
                                        <MoveUp className="w-2.5 h-2.5" />
                                      </button>
                                      <button
                                        type="button"
                                        disabled={wgtIdx === con.children.length - 1}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onMoveNode(wgt.id, 1);
                                        }}
                                        className="p-0.5 hover:text-slate-200 disabled:opacity-20"
                                      >
                                        <MoveDown className="w-2.5 h-2.5" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onDeleteNode(wgt.id);
                                        }}
                                        className="p-0.5 hover:text-red-400"
                                      >
                                        <Trash2 className="w-2.5 h-2.5" />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* WEBSITE FOOTER NODE */}
        <div
          onClick={() => onSelectNode('website-footer')}
          className={`flex items-center justify-between px-2.5 py-2 rounded-lg cursor-pointer transition-colors mt-2 ${
            selectedNodeId === 'website-footer'
              ? 'bg-[#2b3380] text-white font-bold shadow-xs'
              : 'hover:bg-slate-100 text-slate-700 border border-slate-100'
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Layout className={`w-3.5 h-3.5 ${selectedNodeId === 'website-footer' ? 'text-indigo-200' : 'text-[#2b3380]'}`} />
            <span className="truncate text-xs font-semibold">Website Footer</span>
          </div>
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
            selectedNodeId === 'website-footer'
              ? 'bg-white/20 text-white'
              : 'bg-slate-100 text-slate-600'
          }`}>
            Footer
          </span>
        </div>
      </div>
    </div>
  );
}
